import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { imageStudioAPI, getAssetUrl, BASE_URL } from "../lib/api";
import MediaPickerModal, { MediaPickerButton } from "../components/MediaPickerModal";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Slider } from "../components/ui/slider";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Image,
  Loader2,
  Sparkles,
  Download,
  RefreshCw,
  Palette,
  Type,
  Wand2,
  History,
  Trash2,
  Eye,
  Clock,
  ChevronRight,
  Upload,
  Edit3,
  Copy,
  Layers,
  Crop,
  Maximize2,
  Zap,
  Star,
  Heart,
  MoreVertical,
  Grid,
  List,
  Search,
  Filter,
  X,
  Check,
  ImagePlus,
  Scissors,
  Move,
  RotateCcw,
  Sun,
  Contrast,
  Droplet,
  Eraser,
  PaintBucket,
  MonitorSmartphone,
  Instagram,
  Youtube,
  Facebook,
  Twitter,
  Film,
  FolderOpen,
  Plus,
  Settings,
  Share2,
  Users,
  Camera,
} from "lucide-react";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// Style presets for image generation
const stylePresets = [
  { value: "photorealistic", label: "Photorealistic", desc: "Ultra-realistic photography style", icon: "📸" },
  { value: "illustration", label: "Illustration", desc: "Digital art and illustration", icon: "🎨" },
  { value: "3d_render", label: "3D Render", desc: "3D rendered graphics", icon: "🔮" },
  { value: "anime", label: "Anime", desc: "Japanese anime style", icon: "🎌" },
  { value: "watercolor", label: "Watercolor", desc: "Soft watercolor painting", icon: "💧" },
  { value: "logo", label: "Logo/Icon", desc: "Clean logo and icon design", icon: "⭐" },
  { value: "vintage", label: "Vintage", desc: "Retro and vintage aesthetic", icon: "📻" },
  { value: "neon", label: "Neon", desc: "Vibrant neon and cyberpunk", icon: "💜" },
  { value: "minimalist", label: "Minimalist", desc: "Clean and minimal design", icon: "◻️" },
  { value: "cinematic", label: "Cinematic", desc: "Movie-like dramatic lighting", icon: "🎬" },
];

// Aspect ratio presets
const aspectRatios = [
  { value: "1:1", label: "Square", width: 1024, height: 1024, platforms: ["Instagram Post", "Profile"] },
  { value: "9:16", label: "Portrait", width: 1024, height: 1536, platforms: ["TikTok", "Instagram Story", "Reels"] },
  { value: "16:9", label: "Landscape", width: 1536, height: 1024, platforms: ["YouTube", "Twitter", "Facebook"] },
  { value: "4:5", label: "Portrait (4:5)", width: 1024, height: 1280, platforms: ["Instagram Portrait"] },
  { value: "3:4", label: "Standard", width: 1024, height: 1365, platforms: ["Pinterest"] },
];

// Platform resize presets
const platformPresets = [
  { name: "TikTok", icon: "🎵", width: 1080, height: 1920, ratio: "9:16" },
  { name: "Instagram Post", icon: "📸", width: 1080, height: 1080, ratio: "1:1" },
  { name: "Instagram Story", icon: "📱", width: 1080, height: 1920, ratio: "9:16" },
  { name: "YouTube Thumbnail", icon: "▶️", width: 1280, height: 720, ratio: "16:9" },
  { name: "Facebook Cover", icon: "👤", width: 820, height: 312, ratio: "2.6:1" },
  { name: "Twitter Header", icon: "🐦", width: 1500, height: 500, ratio: "3:1" },
  { name: "LinkedIn Banner", icon: "💼", width: 1584, height: 396, ratio: "4:1" },
];

// Enhancement options
const enhanceOptions = [
  { value: "upscale_2x", label: "Upscale 2x", desc: "Double the resolution" },
  { value: "upscale_4x", label: "Upscale 4x", desc: "Quadruple the resolution" },
  { value: "denoise", label: "Denoise", desc: "Remove image noise" },
  { value: "sharpen", label: "Sharpen", desc: "Enhance details and clarity" },
  { value: "color_correct", label: "Color Correct", desc: "Auto color correction" },
  { value: "hdr", label: "HDR Effect", desc: "High dynamic range look" },
];

export default function ImageStudioPage() {
  const [activeTab, setActiveTab] = useState("generate");
  
  // Generate tab state
  const [generatePrompt, setGeneratePrompt] = useState("");
  const [generateStyle, setGenerateStyle] = useState("photorealistic");
  const [generateRatio, setGenerateRatio] = useState("1:1");
  const [negativePrompt, setNegativePrompt] = useState("");
  const [batchCount, setBatchCount] = useState(1);
  
  // Edit tab state
  const [editImage, setEditImage] = useState(null);
  const [editPrompt, setEditPrompt] = useState("");
  const [editMode, setEditMode] = useState("describe"); // describe, remove_bg, replace_bg, remove_object
  
  // Variations tab state
  const [variationImage, setVariationImage] = useState(null);
  const [variationCount, setVariationCount] = useState(4);
  const [variationStrength, setVariationStrength] = useState(0.5);
  const [variationStyle, setVariationStyle] = useState("creative");
  
  // Variation style presets
  const variationStyles = [
    // Product Showcase
    { id: "product_angles", name: "Product Angles", category: "Product Showcase", icon: "📦", description: "Front, side, 45°, top-down views" },
    { id: "product_lifestyle", name: "Lifestyle Context", category: "Product Showcase", icon: "🏠", description: "Product in real-world settings" },
    { id: "product_flatlay", name: "Flat Lay", category: "Product Showcase", icon: "📐", description: "Top-down artistic arrangements" },
    { id: "product_studio", name: "Studio Shots", category: "Product Showcase", icon: "📸", description: "Professional studio lighting setups" },
    
    // Photo Variations
    { id: "pose_variations", name: "Pose Variations", category: "Photo Variations", icon: "🧍", description: "Different poses and positions" },
    { id: "background_swap", name: "Background Swap", category: "Photo Variations", icon: "🖼️", description: "Same subject, different backgrounds" },
    { id: "expression_range", name: "Expression Range", category: "Photo Variations", icon: "😊", description: "Confident, friendly, thoughtful, surprised" },
    
    // Lighting Moods
    { id: "golden_hour", name: "Golden Hour", category: "Lighting Moods", icon: "🌅", description: "Warm sunset/sunrise lighting" },
    { id: "studio_softbox", name: "Studio Softbox", category: "Lighting Moods", icon: "💡", description: "Professional soft studio lighting" },
    { id: "dramatic_shadows", name: "Dramatic Shadows", category: "Lighting Moods", icon: "🌑", description: "High contrast dramatic lighting" },
    { id: "neon_glow", name: "Neon Glow", category: "Lighting Moods", icon: "✨", description: "Vibrant neon color accents" },
    
    // Seasonal/Contextual
    { id: "holiday_theme", name: "Holiday Themes", category: "Seasonal", icon: "🎄", description: "Festive seasonal variations" },
    { id: "summer_vibes", name: "Summer Vibes", category: "Seasonal", icon: "☀️", description: "Bright, warm summer aesthetic" },
    { id: "winter_cozy", name: "Winter Cozy", category: "Seasonal", icon: "❄️", description: "Warm, cozy winter atmosphere" },
    { id: "urban_nature", name: "Urban & Nature", category: "Seasonal", icon: "🌿", description: "City and natural environment mix" },
    
    // Platform Optimized
    { id: "tiktok_style", name: "TikTok Vertical", category: "Platform Optimized", icon: "📱", description: "Optimized for TikTok/Reels" },
    { id: "instagram_square", name: "Instagram Square", category: "Platform Optimized", icon: "📷", description: "Perfect square compositions" },
    { id: "youtube_thumbnail", name: "YouTube Thumbnail", category: "Platform Optimized", icon: "▶️", description: "Eye-catching thumbnail style" },
    
    // Style Transfer
    { id: "cinematic", name: "Cinematic", category: "Style Transfer", icon: "🎬", description: "Movie-like color grading" },
    { id: "vintage_film", name: "Vintage Film", category: "Style Transfer", icon: "📽️", description: "Retro film photography look" },
    { id: "magazine_editorial", name: "Magazine Editorial", category: "Style Transfer", icon: "📰", description: "High-fashion magazine style" },
    { id: "minimalist", name: "Minimalist", category: "Style Transfer", icon: "⬜", description: "Clean, simple compositions" },
    
    // Color Schemes
    { id: "warm_tones", name: "Warm Tones", category: "Color Schemes", icon: "🔥", description: "Orange, red, yellow palette" },
    { id: "cool_tones", name: "Cool Tones", category: "Color Schemes", icon: "💎", description: "Blue, teal, purple palette" },
    { id: "monochromatic", name: "Monochromatic", category: "Color Schemes", icon: "⚫", description: "Single color variations" },
    { id: "vibrant_pop", name: "Vibrant Pop", category: "Color Schemes", icon: "🌈", description: "Bold, saturated colors" },
    
    // General
    { id: "creative", name: "Creative Mix", category: "General", icon: "🎨", description: "AI-driven creative variations" },
  ];
  
  // Group styles by category
  const groupedStyles = variationStyles.reduce((acc, style) => {
    if (!acc[style.category]) acc[style.category] = [];
    acc[style.category].push(style);
    return acc;
  }, {});
  
  // Resize tab state
  const [resizeImage, setResizeImage] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  
  // Enhance tab state
  const [enhanceImage, setEnhanceImage] = useState(null);
  const [enhanceOption, setEnhanceOption] = useState("upscale_2x");
  
  // Thumbnails tab state (keeping existing functionality)
  const [thumbnailTitle, setThumbnailTitle] = useState("");
  const [thumbnailScript, setThumbnailScript] = useState("");
  const [thumbnailStyle, setThumbnailStyle] = useState("vibrant");
  const [includeText, setIncludeText] = useState(false);
  
  // Face Tools tab state
  const [faceToolImage, setFaceToolImage] = useState(null);
  const [faceToolSecondImage, setFaceToolSecondImage] = useState(null);
  const [faceToolMode, setFaceToolMode] = useState("swap"); // swap, expression, age, enhance
  const [faceToolExpression, setFaceToolExpression] = useState("smile");
  const [faceToolAge, setFaceToolAge] = useState("younger"); // younger, older
  
  // Composite tab state
  const [compositeMainImage, setCompositeMainImage] = useState(null);
  const [compositeBackgroundImage, setCompositeBackgroundImage] = useState(null);
  const [compositeMode, setCompositeMode] = useState("background_replace"); // background_replace, overlay_logo, blend, side_by_side
  const [compositeInstruction, setCompositeInstruction] = useState("");
  const [logoPosition, setLogoPosition] = useState("bottom-right");
  const [logoSize, setLogoSize] = useState(20);
  
  // Common state
  const [loading, setLoading] = useState(false);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  
  // Gallery state
  const [gallery, setGallery] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(true);
  const [galleryView, setGalleryView] = useState("grid");
  const [galleryFilter, setGalleryFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showGallery, setShowGallery] = useState(false);
  
  // Dialogs
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);
  const variationFileInputRef = useRef(null);
  const resizeFileInputRef = useRef(null);
  const enhanceFileInputRef = useRef(null);
  const faceToolFileInputRef = useRef(null);
  const faceToolSecondFileInputRef = useRef(null);

  // Fetch gallery on mount
  const fetchGallery = useCallback(async () => {
    try {
      setGalleryLoading(true);
      const response = await imageStudioAPI.getHistory(50, 0);
      setGallery(response.data.images || []);
    } catch (error) {
      console.error("Failed to fetch gallery:", error);
    } finally {
      setGalleryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGallery();
  }, [fetchGallery]);

  // Handle file upload
  const handleFileUpload = (e, setter) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error("File size must be under 10MB");
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        setter({
          file,
          preview: event.target.result,
          name: file.name,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  // Generate image
  const handleGenerate = async () => {
    if (!generatePrompt.trim()) {
      toast.error("Please enter a prompt");
      return;
    }
    
    setLoading(true);
    try {
      const response = await imageStudioAPI.generate({
        prompt: generatePrompt,
        style: generateStyle,
        aspect_ratio: generateRatio,
        negative_prompt: negativePrompt || undefined,
        batch_count: batchCount,
      });
      
      setGeneratedImages(response.data.images || [response.data]);
      toast.success(`Generated ${batchCount} image(s)`);
      fetchGallery();
    } catch (error) {
      console.error("Generation failed:", error);
      toast.error(error.response?.data?.detail || "Failed to generate image");
    } finally {
      setLoading(false);
    }
  };

  // Edit image
  const handleEdit = async () => {
    if (!editImage) {
      toast.error("Please upload an image");
      return;
    }
    if (!editPrompt.trim() && editMode === "describe") {
      toast.error("Please describe the changes");
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("image", editImage.file);
      formData.append("edit_mode", editMode);
      if (editPrompt) formData.append("prompt", editPrompt);
      
      const response = await imageStudioAPI.edit(formData);
      setGeneratedImages([response.data]);
      toast.success("Image edited successfully");
      fetchGallery();
    } catch (error) {
      console.error("Edit failed:", error);
      toast.error(error.response?.data?.detail || "Failed to edit image");
    } finally {
      setLoading(false);
    }
  };

  // Generate variations
  const handleVariations = async () => {
    if (!variationImage) {
      toast.error("Please upload an image");
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("image", variationImage.file);
      formData.append("count", variationCount);
      formData.append("strength", variationStrength);
      formData.append("style", variationStyle);
      
      const response = await imageStudioAPI.variations(formData);
      setGeneratedImages(response.data.images || []);
      const styleName = variationStyles.find(s => s.id === variationStyle)?.name || "Creative";
      toast.success(`Generated ${variationCount} ${styleName} variations`);
      fetchGallery();
    } catch (error) {
      console.error("Variations failed:", error);
      toast.error(error.response?.data?.detail || "Failed to generate variations");
    } finally {
      setLoading(false);
    }
  };

  // Resize image
  const handleResize = async () => {
    if (!resizeImage || !selectedPlatform) {
      toast.error("Please upload an image and select a platform");
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("image", resizeImage.file);
      formData.append("width", selectedPlatform.width);
      formData.append("height", selectedPlatform.height);
      formData.append("platform", selectedPlatform.name);
      
      const response = await imageStudioAPI.resize(formData);
      setGeneratedImages([response.data]);
      toast.success(`Resized for ${selectedPlatform.name}`);
      fetchGallery();
    } catch (error) {
      console.error("Resize failed:", error);
      toast.error(error.response?.data?.detail || "Failed to resize image");
    } finally {
      setLoading(false);
    }
  };

  // Enhance image
  const handleEnhance = async () => {
    if (!enhanceImage) {
      toast.error("Please upload an image");
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("image", enhanceImage.file);
      formData.append("enhancement", enhanceOption);
      
      const response = await imageStudioAPI.enhance(formData);
      setGeneratedImages([response.data]);
      toast.success("Image enhanced successfully");
      fetchGallery();
    } catch (error) {
      console.error("Enhancement failed:", error);
      toast.error(error.response?.data?.detail || "Failed to enhance image");
    } finally {
      setLoading(false);
    }
  };

  // Generate thumbnail (existing functionality)
  const handleGenerateThumbnail = async () => {
    if (!thumbnailTitle.trim()) {
      toast.error("Please enter a title");
      return;
    }
    
    setLoading(true);
    try {
      const response = await imageStudioAPI.generateThumbnail({
        title: thumbnailTitle,
        script: thumbnailScript,
        style: thumbnailStyle,
        include_text: includeText,
      });
      
      setGeneratedImages([response.data]);
      toast.success("Thumbnail generated");
      fetchGallery();
    } catch (error) {
      console.error("Thumbnail generation failed:", error);
      toast.error(error.response?.data?.detail || "Failed to generate thumbnail");
    } finally {
      setLoading(false);
    }
  };

  // Face Tools operations
  const handleFaceTools = async () => {
    if (!faceToolImage) {
      toast.error("Please upload an image");
      return;
    }
    if (faceToolMode === "swap" && !faceToolSecondImage) {
      toast.error("Please upload a second image for face swap");
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("image", faceToolImage.file);
      formData.append("mode", faceToolMode);
      
      if (faceToolMode === "swap" && faceToolSecondImage) {
        formData.append("target_image", faceToolSecondImage.file);
      }
      if (faceToolMode === "expression") {
        formData.append("expression", faceToolExpression);
      }
      if (faceToolMode === "age") {
        formData.append("age_direction", faceToolAge);
      }
      
      const response = await imageStudioAPI.faceTools(formData);
      setGeneratedImages([response.data]);
      toast.success(`Face ${faceToolMode} completed`);
      fetchGallery();
    } catch (error) {
      console.error("Face tools failed:", error);
      toast.error(error.response?.data?.detail || "Face tools operation failed");
    } finally {
      setLoading(false);
    }
  };

  // Download image
  const handleDownload = async (imageUrl, filename = "image.png") => {
    try {
      const response = await fetch(getAssetUrl(imageUrl));
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Image downloaded");
    } catch (error) {
      toast.error("Failed to download image");
    }
  };

  // Delete image
  const handleDelete = async () => {
    if (!imageToDelete) return;
    
    try {
      await imageStudioAPI.delete(imageToDelete.id);
      setGallery(prev => prev.filter(img => img.id !== imageToDelete.id));
      toast.success("Image deleted");
    } catch (error) {
      toast.error("Failed to delete image");
    } finally {
      setDeleteDialogOpen(false);
      setImageToDelete(null);
    }
  };

  // Use image in video
  const navigate = useNavigate();

  const handleUseInVideo = (image, destination = "create") => {
    const imageData = {
      url: getAssetUrl(image.url || image.image_url),
      id: image.id,
      type: image.type,
    };
    
    // Store in localStorage for the destination page
    localStorage.setItem("pending_video_image", JSON.stringify(imageData));
    
    if (destination === "ugc") {
      navigate("/ugc-studio", { state: { productImage: imageData } });
      toast.success("Navigating to UGC Ad Studio with your image");
    } else {
      navigate("/create", { state: { referenceImage: imageData } });
      toast.success("Navigating to Create Video with your image");
    }
  };

  // Render upload zone
  // Handle selecting image from Recent Images for current tool
  const handleSelectRecentImage = async (libraryImage) => {
    try {
      const imageUrl = getAssetUrl(libraryImage.url || libraryImage.image_url);
      
      // Fetch the image and convert to File object
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const fileName = `library_${libraryImage.id || Date.now()}.${blob.type.split('/')[1] || 'png'}`;
      const file = new File([blob], fileName, { type: blob.type });
      
      const imageData = {
        file,
        preview: imageUrl,
        name: fileName,
        fromLibrary: true,
      };
      
      // Set the image based on current active tab
      switch (activeTab) {
        case "edit":
          setEditImage(imageData);
          break;
        case "variations":
          setVariationImage(imageData);
          break;
        case "resize":
          setResizeImage(imageData);
          break;
        case "enhance":
          setEnhanceImage(imageData);
          break;
        case "face":
          // For face tools, set primary image (user can upload second if needed)
          setFaceToolImage(imageData);
          break;
        case "composite":
          setCompositeMainImage(imageData);
          break;
        default:
          // For generate tab, show preview instead
          setPreviewImage(libraryImage);
          return;
      }
      
      toast.success("Image selected for " + activeTab);
    } catch (error) {
      console.error("Failed to load image:", error);
      toast.error("Failed to load image");
    }
  };

  // Handle selecting image from MediaPicker
  const handleMediaPickerSelect = (imageData, setImageFunc) => {
    if (imageData.url || imageData.thumbnail_url) {
      const imageUrl = imageData.url || imageData.thumbnail_url;
      // Fetch and convert to file object for consistency
      fetch(imageUrl)
        .then(res => res.blob())
        .then(blob => {
          const file = new File([blob], imageData.name || "media-library.jpg", { type: blob.type || "image/jpeg" });
          setImageFunc({
            file,
            preview: imageUrl,
            name: imageData.name || "Media Library Image",
            fromLibrary: true,
          });
          toast.success("Image selected from Media Library");
        })
        .catch(err => {
          console.error("Failed to load image:", err);
          toast.error("Failed to load image from Media Library");
        });
    } else if (imageData.isUpload && imageData.file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageFunc({
          file: imageData.file,
          preview: reader.result,
          name: imageData.file.name,
          fromLibrary: false,
        });
        toast.success("Image uploaded successfully");
      };
      reader.readAsDataURL(imageData.file);
    }
  };

  const renderUploadZone = (image, setImage, fileInputRef, label = "Upload Image") => (
    <div className="space-y-2">
      <div
        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
          image ? "border-[#00F0FF]/50 bg-[#00F0FF]/5" : "border-white/20 hover:border-white/40"
        }`}
        onClick={() => fileInputRef.current?.click()}
      >
        {image ? (
          <div className="space-y-4">
            <img
              src={image.preview}
              alt="Uploaded"
              className="max-h-48 mx-auto rounded-lg object-contain"
            />
            <p className="text-sm text-muted-foreground">
              {image.name}
              {image.fromLibrary && <Badge variant="secondary" className="ml-2 text-xs">From Library</Badge>}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setImage(null);
              }}
            >
              <X className="w-4 h-4 mr-2" />
              Remove
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <Upload className="w-12 h-12 mx-auto text-muted-foreground" />
            <p className="text-white font-medium">{label}</p>
            <p className="text-sm text-muted-foreground">
              Drag & drop or click to browse
            </p>
            <p className="text-xs text-muted-foreground">PNG, JPG, WebP up to 10MB</p>
          </div>
        )}
      </div>
      {!image && (
        <MediaPickerButton
          onSelect={(data) => handleMediaPickerSelect(data, setImage)}
          mediaType="image"
          multiple={false}
          allowUpload={true}
          buttonText="Pick from Media Library"
          buttonClassName="w-full flex items-center justify-center gap-2 px-3 py-2 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 rounded-lg text-sm text-purple-300 transition-colors"
        />
      )}
    </div>
  );

  // Render generated images
  const renderGeneratedImages = () => {
    if (generatedImages.length === 0) return null;
    
    return (
      <Card className="bg-secondary/30 border-border/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#00F0FF]" />
            Generated Images
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className={`grid gap-4 ${generatedImages.length === 1 ? "grid-cols-1 max-w-md mx-auto" : "grid-cols-2 md:grid-cols-4"}`}>
            {generatedImages.map((img, idx) => (
              <motion.div
                key={img.id || idx}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="relative group rounded-lg overflow-hidden bg-black"
              >
                <img
                  src={getAssetUrl(img.thumbnail_url || img.url || img.image_url)}
                        loading="lazy"
                  alt={`Generated ${idx + 1}`}
                  className="w-full aspect-square object-cover"
                />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setPreviewImage(img)}
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => handleDownload(img.url || img.image_url)}
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => handleUseInVideo(img)}
                  >
                    <Film className="w-4 h-4" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <motion.div
      className="min-h-screen p-6 space-y-6"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#00F0FF] to-[#FF0055] flex items-center justify-center">
              <Wand2 className="w-6 h-6 text-white" />
            </div>
            AI Image Studio
          </h1>
          <p className="text-muted-foreground mt-1">
            Generate, edit, and enhance images with AI
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            className="border-white/20"
            onClick={() => setShowGallery(!showGallery)}
          >
            <FolderOpen className="w-4 h-4 mr-2" />
            Gallery
            <Badge className="ml-2 bg-[#00F0FF]/20 text-[#00F0FF]">{gallery.length}</Badge>
          </Button>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel - Tools */}
        <motion.div variants={itemVariants} className="lg:col-span-2">
          <Card className="bg-[#111113] border-border/50">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <div className="border-b border-border/50">
                <TabsList className="bg-transparent h-14 p-0 gap-0 w-full justify-start overflow-x-auto">
                  <TabsTrigger value="generate" className="data-[state=active]:bg-[#00F0FF]/10 data-[state=active]:text-[#00F0FF] rounded-none px-4 h-14 border-b-2 border-transparent data-[state=active]:border-[#00F0FF]">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate
                  </TabsTrigger>
                  <TabsTrigger value="edit" className="data-[state=active]:bg-[#00F0FF]/10 data-[state=active]:text-[#00F0FF] rounded-none px-4 h-14 border-b-2 border-transparent data-[state=active]:border-[#00F0FF]">
                    <Edit3 className="w-4 h-4 mr-2" />
                    Edit
                  </TabsTrigger>
                  <TabsTrigger value="variations" className="data-[state=active]:bg-[#00F0FF]/10 data-[state=active]:text-[#00F0FF] rounded-none px-4 h-14 border-b-2 border-transparent data-[state=active]:border-[#00F0FF]">
                    <Copy className="w-4 h-4 mr-2" />
                    Variations
                  </TabsTrigger>
                  <TabsTrigger value="resize" className="data-[state=active]:bg-[#00F0FF]/10 data-[state=active]:text-[#00F0FF] rounded-none px-4 h-14 border-b-2 border-transparent data-[state=active]:border-[#00F0FF]">
                    <Crop className="w-4 h-4 mr-2" />
                    Resize
                  </TabsTrigger>
                  <TabsTrigger value="enhance" className="data-[state=active]:bg-[#00F0FF]/10 data-[state=active]:text-[#00F0FF] rounded-none px-4 h-14 border-b-2 border-transparent data-[state=active]:border-[#00F0FF]">
                    <Zap className="w-4 h-4 mr-2" />
                    Enhance
                  </TabsTrigger>
                  <TabsTrigger value="face-tools" className="data-[state=active]:bg-[#00F0FF]/10 data-[state=active]:text-[#00F0FF] rounded-none px-4 h-14 border-b-2 border-transparent data-[state=active]:border-[#00F0FF]">
                    <Users className="w-4 h-4 mr-2" />
                    Face Tools
                  </TabsTrigger>
                  <TabsTrigger value="composite" className="data-[state=active]:bg-[#A855F7]/10 data-[state=active]:text-[#A855F7] rounded-none px-4 h-14 border-b-2 border-transparent data-[state=active]:border-[#A855F7]">
                    <Layers className="w-4 h-4 mr-2" />
                    Composite
                  </TabsTrigger>
                  <TabsTrigger value="thumbnails" className="data-[state=active]:bg-[#00F0FF]/10 data-[state=active]:text-[#00F0FF] rounded-none px-4 h-14 border-b-2 border-transparent data-[state=active]:border-[#00F0FF]">
                    <Image className="w-4 h-4 mr-2" />
                    Thumbnails
                  </TabsTrigger>
                </TabsList>
              </div>

              <div className="p-6">
                {/* Generate Tab */}
                <TabsContent value="generate" className="mt-0 space-y-6">
                  <div>
                    <Label className="text-white mb-2 block">Prompt</Label>
                    <Textarea
                      value={generatePrompt}
                      onChange={(e) => setGeneratePrompt(e.target.value)}
                      placeholder="Describe the image you want to create..."
                      className="bg-secondary/50 border-border/50 min-h-[100px] resize-none"
                    />
                  </div>

                  <div>
                    <Label className="text-white mb-2 block">Style</Label>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                      {stylePresets.map((style) => (
                        <button
                          key={style.value}
                          onClick={() => setGenerateStyle(style.value)}
                          className={`p-3 rounded-lg border text-left transition-all ${
                            generateStyle === style.value
                              ? "border-[#00F0FF] bg-[#00F0FF]/10"
                              : "border-border/50 hover:border-white/30"
                          }`}
                        >
                          <span className="text-xl">{style.icon}</span>
                          <p className="text-sm text-white mt-1">{style.label}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white mb-2 block">Aspect Ratio</Label>
                      <Select value={generateRatio} onValueChange={setGenerateRatio}>
                        <SelectTrigger className="bg-secondary/50 border-border/50">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {aspectRatios.map((ratio) => (
                            <SelectItem key={ratio.value} value={ratio.value}>
                              {ratio.label} ({ratio.value})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-white mb-2 block">Batch Count</Label>
                      <Select value={batchCount.toString()} onValueChange={(v) => setBatchCount(parseInt(v))}>
                        <SelectTrigger className="bg-secondary/50 border-border/50">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 2, 4].map((count) => (
                            <SelectItem key={count} value={count.toString()}>
                              {count} image{count > 1 ? "s" : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label className="text-white mb-2 block">Negative Prompt (Optional)</Label>
                    <Input
                      value={negativePrompt}
                      onChange={(e) => setNegativePrompt(e.target.value)}
                      placeholder="What to avoid in the image..."
                      className="bg-secondary/50 border-border/50"
                    />
                  </div>

                  <Button
                    onClick={handleGenerate}
                    disabled={loading || !generatePrompt.trim()}
                    className="w-full bg-gradient-to-r from-[#00F0FF] to-[#FF0055] hover:opacity-90 text-white h-12"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5 mr-2" />
                        Generate Image{batchCount > 1 ? "s" : ""}
                      </>
                    )}
                  </Button>
                </TabsContent>

                {/* Edit Tab */}
                <TabsContent value="edit" className="mt-0 space-y-6">
                  <input
                    type="file"
                    ref={editFileInputRef}
                    className="hidden"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e, setEditImage)}
                  />
                  
                  {renderUploadZone(editImage, setEditImage, editFileInputRef, "Upload Image to Edit")}

                  <div>
                    <Label className="text-white mb-2 block">Edit Mode</Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {[
                        { value: "describe", label: "Describe Changes", icon: <Edit3 className="w-4 h-4" /> },
                        { value: "remove_bg", label: "Remove Background", icon: <Eraser className="w-4 h-4" /> },
                        { value: "replace_bg", label: "Replace Background", icon: <PaintBucket className="w-4 h-4" /> },
                        { value: "remove_object", label: "Remove Object", icon: <Scissors className="w-4 h-4" /> },
                      ].map((mode) => (
                        <button
                          key={mode.value}
                          onClick={() => setEditMode(mode.value)}
                          className={`p-3 rounded-lg border flex items-center gap-2 transition-all ${
                            editMode === mode.value
                              ? "border-[#00F0FF] bg-[#00F0FF]/10"
                              : "border-border/50 hover:border-white/30"
                          }`}
                        >
                          {mode.icon}
                          <span className="text-sm text-white">{mode.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {(editMode === "describe" || editMode === "replace_bg" || editMode === "remove_object") && (
                    <div>
                      <Label className="text-white mb-2 block">
                        {editMode === "describe" && "Describe the changes you want"}
                        {editMode === "replace_bg" && "Describe the new background"}
                        {editMode === "remove_object" && "Describe what to remove"}
                      </Label>
                      <Textarea
                        value={editPrompt}
                        onChange={(e) => setEditPrompt(e.target.value)}
                        placeholder={
                          editMode === "describe" ? "Make the person smile, change hair color to blonde..." :
                          editMode === "replace_bg" ? "A sunny beach, mountains at sunset..." :
                          "Remove the car in the background..."
                        }
                        className="bg-secondary/50 border-border/50 min-h-[80px] resize-none"
                      />
                    </div>
                  )}

                  <Button
                    onClick={handleEdit}
                    disabled={loading || !editImage}
                    className="w-full bg-gradient-to-r from-[#00F0FF] to-[#FF0055] hover:opacity-90 text-white h-12"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Edit3 className="w-5 h-5 mr-2" />
                        Apply Edit
                      </>
                    )}
                  </Button>
                </TabsContent>

                {/* Variations Tab */}
                <TabsContent value="variations" className="mt-0 space-y-6">
                  <input
                    type="file"
                    ref={variationFileInputRef}
                    className="hidden"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e, setVariationImage)}
                  />
                  
                  {renderUploadZone(variationImage, setVariationImage, variationFileInputRef, "Upload Reference Image")}

                  {/* Variation Style Selection */}
                  <div>
                    <Label className="text-white mb-3 block text-lg font-semibold">Variation Style</Label>
                    <div className="max-h-[300px] overflow-y-auto pr-2 space-y-4">
                      {Object.entries(groupedStyles).map(([category, styles]) => (
                        <div key={category}>
                          <p className="text-xs text-[#00F0FF] uppercase tracking-wider mb-2 font-medium">{category}</p>
                          <div className="grid grid-cols-2 gap-2">
                            {styles.map((style) => (
                              <button
                                key={style.id}
                                onClick={() => setVariationStyle(style.id)}
                                className={`p-3 rounded-lg border text-left transition-all ${
                                  variationStyle === style.id
                                    ? "border-[#00F0FF] bg-[#00F0FF]/10"
                                    : "border-border/30 bg-secondary/30 hover:border-border/60"
                                }`}
                                data-testid={`variation-style-${style.id}`}
                              >
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-lg">{style.icon}</span>
                                  <span className="text-sm font-medium text-white">{style.name}</span>
                                </div>
                                <p className="text-xs text-muted-foreground line-clamp-1">{style.description}</p>
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white mb-2 block">Number of Variations</Label>
                      <Select value={variationCount.toString()} onValueChange={(v) => setVariationCount(parseInt(v))}>
                        <SelectTrigger className="bg-secondary/50 border-border/50">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[2, 4, 6, 8].map((count) => (
                            <SelectItem key={count} value={count.toString()}>
                              {count} variations
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-white mb-2 block">Variation Strength: {Math.round(variationStrength * 100)}%</Label>
                      <Slider
                        value={[variationStrength]}
                        onValueChange={([v]) => setVariationStrength(v)}
                        min={0.1}
                        max={0.9}
                        step={0.1}
                        className="mt-2"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Lower = closer to original, Higher = more creative
                      </p>
                    </div>
                  </div>

                  {/* Selected style preview */}
                  {variationStyle && (
                    <div className="p-3 rounded-lg bg-[#00F0FF]/5 border border-[#00F0FF]/20">
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{variationStyles.find(s => s.id === variationStyle)?.icon}</span>
                        <div>
                          <p className="text-sm font-medium text-white">{variationStyles.find(s => s.id === variationStyle)?.name}</p>
                          <p className="text-xs text-muted-foreground">{variationStyles.find(s => s.id === variationStyle)?.description}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <Button
                    onClick={handleVariations}
                    disabled={loading || !variationImage}
                    className="w-full bg-gradient-to-r from-[#00F0FF] to-[#FF0055] hover:opacity-90 text-white h-12"
                    data-testid="generate-variations-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Generating {variationStyles.find(s => s.id === variationStyle)?.name}...
                      </>
                    ) : (
                      <>
                        <Copy className="w-5 h-5 mr-2" />
                        Generate {variationStyles.find(s => s.id === variationStyle)?.name} Variations
                      </>
                    )}
                  </Button>
                </TabsContent>

                {/* Resize Tab */}
                <TabsContent value="resize" className="mt-0 space-y-6">
                  <input
                    type="file"
                    ref={resizeFileInputRef}
                    className="hidden"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e, setResizeImage)}
                  />
                  
                  {renderUploadZone(resizeImage, setResizeImage, resizeFileInputRef, "Upload Image to Resize")}

                  <div>
                    <Label className="text-white mb-2 block">Select Platform</Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {platformPresets.map((platform) => (
                        <button
                          key={platform.name}
                          onClick={() => setSelectedPlatform(platform)}
                          className={`p-4 rounded-lg border text-left transition-all ${
                            selectedPlatform?.name === platform.name
                              ? "border-[#00F0FF] bg-[#00F0FF]/10"
                              : "border-border/50 hover:border-white/30"
                          }`}
                        >
                          <span className="text-2xl">{platform.icon}</span>
                          <p className="text-sm text-white mt-2 font-medium">{platform.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {platform.width} x {platform.height}
                          </p>
                        </button>
                      ))}
                    </div>
                  </div>

                  <Button
                    onClick={handleResize}
                    disabled={loading || !resizeImage || !selectedPlatform}
                    className="w-full bg-gradient-to-r from-[#00F0FF] to-[#FF0055] hover:opacity-90 text-white h-12"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Resizing...
                      </>
                    ) : (
                      <>
                        <Crop className="w-5 h-5 mr-2" />
                        Resize for {selectedPlatform?.name || "Platform"}
                      </>
                    )}
                  </Button>
                </TabsContent>

                {/* Enhance Tab */}
                <TabsContent value="enhance" className="mt-0 space-y-6">
                  <input
                    type="file"
                    ref={enhanceFileInputRef}
                    className="hidden"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e, setEnhanceImage)}
                  />
                  
                  {renderUploadZone(enhanceImage, setEnhanceImage, enhanceFileInputRef, "Upload Image to Enhance")}

                  <div>
                    <Label className="text-white mb-2 block">Enhancement Type</Label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {enhanceOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => setEnhanceOption(option.value)}
                          className={`p-4 rounded-lg border text-left transition-all ${
                            enhanceOption === option.value
                              ? "border-[#00F0FF] bg-[#00F0FF]/10"
                              : "border-border/50 hover:border-white/30"
                          }`}
                        >
                          <p className="text-sm text-white font-medium">{option.label}</p>
                          <p className="text-xs text-muted-foreground mt-1">{option.desc}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  <Button
                    onClick={handleEnhance}
                    disabled={loading || !enhanceImage}
                    className="w-full bg-gradient-to-r from-[#00F0FF] to-[#FF0055] hover:opacity-90 text-white h-12"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Enhancing...
                      </>
                    ) : (
                      <>
                        <Zap className="w-5 h-5 mr-2" />
                        Enhance Image
                      </>
                    )}
                  </Button>
                </TabsContent>

                {/* Face Tools Tab */}
                <TabsContent value="face-tools" className="mt-0 space-y-6">
                  <input
                    type="file"
                    ref={faceToolFileInputRef}
                    className="hidden"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e, setFaceToolImage)}
                  />
                  <input
                    type="file"
                    ref={faceToolSecondFileInputRef}
                    className="hidden"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e, setFaceToolSecondImage)}
                  />

                  <div>
                    <Label className="text-white mb-2 block">Face Tool Mode</Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { value: "swap", label: "Face Swap", desc: "Swap faces between two images", icon: "🔄" },
                        { value: "expression", label: "Expression Change", desc: "Change facial expression", icon: "😊" },
                        { value: "age", label: "Age Transform", desc: "Make younger or older", icon: "⏳" },
                        { value: "enhance", label: "Face Enhance", desc: "Fix lighting & smooth skin", icon: "✨" },
                      ].map((mode) => (
                        <button
                          key={mode.value}
                          onClick={() => setFaceToolMode(mode.value)}
                          className={`p-4 rounded-lg border text-left transition-all ${
                            faceToolMode === mode.value
                              ? "border-[#00F0FF] bg-[#00F0FF]/10"
                              : "border-border/50 hover:border-white/30"
                          }`}
                        >
                          <span className="text-2xl">{mode.icon}</span>
                          <p className="text-sm text-white mt-2 font-medium">{mode.label}</p>
                          <p className="text-xs text-muted-foreground">{mode.desc}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Primary Image Upload */}
                  <div>
                    <Label className="text-white mb-2 block">
                      {faceToolMode === "swap" ? "Source Face (face to use)" : "Image with Face"}
                    </Label>
                    {renderUploadZone(faceToolImage, setFaceToolImage, faceToolFileInputRef, 
                      faceToolMode === "swap" ? "Upload source face image" : "Upload image with face"
                    )}
                  </div>

                  {/* Second Image for Face Swap */}
                  {faceToolMode === "swap" && (
                    <div>
                      <Label className="text-white mb-2 block">Target Image (where to swap face)</Label>
                      {renderUploadZone(faceToolSecondImage, setFaceToolSecondImage, faceToolSecondFileInputRef, 
                        "Upload target image"
                      )}
                    </div>
                  )}

                  {/* Expression Options */}
                  {faceToolMode === "expression" && (
                    <div>
                      <Label className="text-white mb-2 block">Target Expression</Label>
                      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                        {[
                          { value: "smile", label: "Smile", icon: "😊" },
                          { value: "laugh", label: "Laugh", icon: "😄" },
                          { value: "serious", label: "Serious", icon: "😐" },
                          { value: "surprised", label: "Surprised", icon: "😮" },
                          { value: "wink", label: "Wink", icon: "😉" },
                          { value: "confident", label: "Confident", icon: "😎" },
                        ].map((exp) => (
                          <button
                            key={exp.value}
                            onClick={() => setFaceToolExpression(exp.value)}
                            className={`p-3 rounded-lg border text-center transition-all ${
                              faceToolExpression === exp.value
                                ? "border-[#00F0FF] bg-[#00F0FF]/10"
                                : "border-border/50 hover:border-white/30"
                            }`}
                          >
                            <span className="text-2xl">{exp.icon}</span>
                            <p className="text-xs text-white mt-1">{exp.label}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Age Options */}
                  {faceToolMode === "age" && (
                    <div>
                      <Label className="text-white mb-2 block">Age Direction</Label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          onClick={() => setFaceToolAge("younger")}
                          className={`p-4 rounded-lg border text-center transition-all ${
                            faceToolAge === "younger"
                              ? "border-[#00F0FF] bg-[#00F0FF]/10"
                              : "border-border/50 hover:border-white/30"
                          }`}
                        >
                          <span className="text-3xl">👶</span>
                          <p className="text-sm text-white mt-2 font-medium">Make Younger</p>
                          <p className="text-xs text-muted-foreground">Reduce apparent age by ~10-15 years</p>
                        </button>
                        <button
                          onClick={() => setFaceToolAge("older")}
                          className={`p-4 rounded-lg border text-center transition-all ${
                            faceToolAge === "older"
                              ? "border-[#00F0FF] bg-[#00F0FF]/10"
                              : "border-border/50 hover:border-white/30"
                          }`}
                        >
                          <span className="text-3xl">👴</span>
                          <p className="text-sm text-white mt-2 font-medium">Make Older</p>
                          <p className="text-xs text-muted-foreground">Increase apparent age by ~10-15 years</p>
                        </button>
                      </div>
                    </div>
                  )}

                  <Button
                    onClick={handleFaceTools}
                    disabled={loading || !faceToolImage || (faceToolMode === "swap" && !faceToolSecondImage)}
                    className="w-full bg-gradient-to-r from-[#00F0FF] to-[#FF0055] hover:opacity-90 text-white h-12"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Users className="w-5 h-5 mr-2" />
                        {faceToolMode === "swap" && "Swap Faces"}
                        {faceToolMode === "expression" && `Change to ${faceToolExpression}`}
                        {faceToolMode === "age" && `Make ${faceToolAge === "younger" ? "Younger" : "Older"}`}
                        {faceToolMode === "enhance" && "Enhance Face"}
                      </>
                    )}
                  </Button>
                </TabsContent>

                {/* Composite Tab */}
                <TabsContent value="composite" className="mt-0 space-y-6">
                  <div>
                    <Label className="text-white mb-2 block">Composite Mode</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { id: "background_replace", label: "Replace Background", desc: "Replace background with second image" },
                        { id: "overlay_logo", label: "Add Logo/Watermark", desc: "Overlay logo on image" },
                        { id: "blend", label: "AI Blend", desc: "Intelligently blend two images" },
                        { id: "side_by_side", label: "Side by Side", desc: "Place images next to each other" },
                      ].map((mode) => (
                        <button
                          key={mode.id}
                          onClick={() => setCompositeMode(mode.id)}
                          className={`p-3 rounded-lg border-2 text-left transition-all ${
                            compositeMode === mode.id
                              ? "border-[#A855F7] bg-[#A855F7]/10"
                              : "border-border/50 bg-secondary/30 hover:border-border"
                          }`}
                        >
                          <p className="text-white text-sm font-medium">{mode.label}</p>
                          <p className="text-muted-foreground text-xs">{mode.desc}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* Main Image Upload */}
                    <div>
                      <Label className="text-white mb-2 block">Main Image (Subject)</Label>
                      <div
                        className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                          compositeMainImage ? "border-[#A855F7]" : "border-border/50 hover:border-border"
                        }`}
                        onClick={() => document.getElementById("composite-main-upload").click()}
                      >
                        {compositeMainImage ? (
                          <div className="relative">
                            <img src={compositeMainImage} alt="Main" className="max-h-32 mx-auto rounded" />
                            <button
                              onClick={(e) => { e.stopPropagation(); setCompositeMainImage(null); }}
                              className="absolute top-1 right-1 bg-red-500 rounded-full p-1"
                            >
                              <X className="w-3 h-3 text-white" />
                            </button>
                          </div>
                        ) : (
                          <>
                            <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                            <p className="text-sm text-muted-foreground">Upload main image</p>
                          </>
                        )}
                      </div>
                      <input
                        id="composite-main-upload"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onload = (ev) => setCompositeMainImage(ev.target.result);
                            reader.readAsDataURL(file);
                          }
                        }}
                      />
                    </div>

                    {/* Background/Logo Image Upload */}
                    <div>
                      <Label className="text-white mb-2 block">
                        {compositeMode === "overlay_logo" ? "Logo Image" : "Background Image"}
                      </Label>
                      <div
                        className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                          compositeBackgroundImage ? "border-[#A855F7]" : "border-border/50 hover:border-border"
                        }`}
                        onClick={() => document.getElementById("composite-bg-upload").click()}
                      >
                        {compositeBackgroundImage ? (
                          <div className="relative">
                            <img src={compositeBackgroundImage} alt="Background" className="max-h-32 mx-auto rounded" />
                            <button
                              onClick={(e) => { e.stopPropagation(); setCompositeBackgroundImage(null); }}
                              className="absolute top-1 right-1 bg-red-500 rounded-full p-1"
                            >
                              <X className="w-3 h-3 text-white" />
                            </button>
                          </div>
                        ) : (
                          <>
                            <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                            <p className="text-sm text-muted-foreground">
                              Upload {compositeMode === "overlay_logo" ? "logo" : "background"}
                            </p>
                          </>
                        )}
                      </div>
                      <input
                        id="composite-bg-upload"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onload = (ev) => setCompositeBackgroundImage(ev.target.result);
                            reader.readAsDataURL(file);
                          }
                        }}
                      />
                    </div>
                  </div>

                  {/* Logo-specific options */}
                  {compositeMode === "overlay_logo" && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-white mb-2 block">Logo Position</Label>
                        <select
                          value={logoPosition}
                          onChange={(e) => setLogoPosition(e.target.value)}
                          className="w-full bg-secondary/50 border border-border/50 rounded-lg px-3 py-2 text-white"
                        >
                          <option value="top-left">Top Left</option>
                          <option value="top-right">Top Right</option>
                          <option value="bottom-left">Bottom Left</option>
                          <option value="bottom-right">Bottom Right</option>
                          <option value="center">Center</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-white mb-2 block">Logo Size: {logoSize}%</Label>
                        <input
                          type="range"
                          min="10"
                          max="50"
                          value={logoSize}
                          onChange={(e) => setLogoSize(parseInt(e.target.value))}
                          className="w-full"
                        />
                      </div>
                    </div>
                  )}

                  {/* Instructions for AI modes */}
                  {(compositeMode === "background_replace" || compositeMode === "blend") && (
                    <div>
                      <Label className="text-white mb-2 block">Instructions (Optional)</Label>
                      <Textarea
                        value={compositeInstruction}
                        onChange={(e) => setCompositeInstruction(e.target.value)}
                        placeholder={compositeMode === "background_replace" 
                          ? "E.g., Keep the person sharp and blend naturally with the new background..." 
                          : "E.g., Blend the product into the lifestyle scene..."}
                        className="bg-secondary/50 border-border/50 min-h-[80px]"
                      />
                    </div>
                  )}

                  <Button
                    onClick={async () => {
                      if (!compositeMainImage || !compositeBackgroundImage) {
                        toast.error("Please upload both images");
                        return;
                      }
                      
                      setLoading(true);
                      try {
                        const formData = new FormData();
                        
                        // Convert data URLs to blobs
                        const mainBlob = await fetch(compositeMainImage).then(r => r.blob());
                        const bgBlob = await fetch(compositeBackgroundImage).then(r => r.blob());
                        
                        formData.append("main_image", mainBlob, "main.png");
                        formData.append("background_image", bgBlob, "background.png");
                        formData.append("mode", compositeMode);
                        formData.append("instruction", compositeInstruction || "Create a professional composite");
                        formData.append("logo_position", logoPosition);
                        formData.append("logo_size", logoSize.toString());
                        
                        const response = await fetch(`${BASE_URL}/api/image-studio/composite`, {
                          method: "POST",
                          headers: { Authorization: `Bearer ${localStorage.getItem("auth_token")}` },
                          body: formData,
                        });
                        
                        if (!response.ok) {
                          const error = await response.json();
                          throw new Error(error.detail || "Composite failed");
                        }
                        
                        const result = await response.json();
                        setGeneratedImages([result]);
                        toast.success("Composite created!");
                      } catch (error) {
                        toast.error(error.message || "Failed to create composite");
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={loading || !compositeMainImage || !compositeBackgroundImage}
                    className="w-full bg-gradient-to-r from-[#A855F7] to-[#FF0055] hover:opacity-90 h-12"
                  >
                    {loading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                    ) : (
                      <><Layers className="w-4 h-4 mr-2" /> Create Composite</>
                    )}
                  </Button>
                </TabsContent>

                {/* Thumbnails Tab */}
                <TabsContent value="thumbnails" className="mt-0 space-y-6">
                  <div>
                    <Label className="text-white mb-2 block">Video Title</Label>
                    <Input
                      value={thumbnailTitle}
                      onChange={(e) => setThumbnailTitle(e.target.value)}
                      placeholder="Enter your video title..."
                      className="bg-secondary/50 border-border/50"
                    />
                  </div>

                  <div>
                    <Label className="text-white mb-2 block">Script/Description (Optional)</Label>
                    <Textarea
                      value={thumbnailScript}
                      onChange={(e) => setThumbnailScript(e.target.value)}
                      placeholder="Brief description of your video content..."
                      className="bg-secondary/50 border-border/50 min-h-[80px] resize-none"
                    />
                  </div>

                  <div>
                    <Label className="text-white mb-2 block">Style</Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {[
                        { value: "vibrant", label: "Vibrant", color: "bg-[#FF0055]/20 text-[#FF0055]" },
                        { value: "minimal", label: "Minimal", color: "bg-[#00F0FF]/20 text-[#00F0FF]" },
                        { value: "dramatic", label: "Dramatic", color: "bg-[#A855F7]/20 text-[#A855F7]" },
                        { value: "aesthetic", label: "Aesthetic", color: "bg-[#F59E0B]/20 text-[#F59E0B]" },
                      ].map((style) => (
                        <button
                          key={style.value}
                          onClick={() => setThumbnailStyle(style.value)}
                          className={`p-3 rounded-lg border transition-all ${
                            thumbnailStyle === style.value
                              ? `border-white/50 ${style.color}`
                              : "border-border/50 hover:border-white/30"
                          }`}
                        >
                          <p className="text-sm font-medium">{style.label}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Switch
                      checked={includeText}
                      onCheckedChange={setIncludeText}
                    />
                    <Label className="text-white">Include text overlay</Label>
                  </div>

                  <Button
                    onClick={handleGenerateThumbnail}
                    disabled={loading || !thumbnailTitle.trim()}
                    className="w-full bg-gradient-to-r from-[#00F0FF] to-[#FF0055] hover:opacity-90 text-white h-12"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Image className="w-5 h-5 mr-2" />
                        Generate Thumbnail
                      </>
                    )}
                  </Button>
                </TabsContent>
              </div>
            </Tabs>
          </Card>

          {/* Generated Images */}
          {renderGeneratedImages()}
        </motion.div>

        {/* Right Panel - Gallery */}
        <motion.div variants={itemVariants} className={`${showGallery ? "block" : "hidden lg:block"}`}>
          <Card className="bg-[#111113] border-border/50 sticky top-6">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <History className="w-5 h-5 text-[#00F0FF]" />
                  Recent Images
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={fetchGallery}>
                  <RefreshCw className={`w-4 h-4 ${galleryLoading ? "animate-spin" : ""}`} />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="max-h-[600px] overflow-y-auto">
              {galleryLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-[#00F0FF] animate-spin" />
                </div>
              ) : gallery.length > 0 ? (
                <div className="grid grid-cols-2 gap-2">
                  {gallery.slice(0, 12).map((img) => (
                    <div
                      key={img.id}
                      className="relative group rounded-lg overflow-hidden bg-black aspect-square cursor-pointer"
                      onClick={() => setPreviewImage(img)}
                      data-testid={`recent-image-${img.id}`}
                    >
                      <img
                        src={getAssetUrl(img.thumbnail_url || img.url || img.image_url)}
                        loading="lazy"
                        alt=""
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                        <Eye className="w-5 h-5 text-white" />
                        <Button
                          size="sm"
                          className="bg-[#00F0FF] hover:bg-[#00F0FF]/80 text-black text-xs px-3 py-1 h-7"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectRecentImage(img);
                          }}
                        >
                          Use in Tool
                        </Button>
                      </div>
                      {img.type && (
                        <Badge className="absolute top-1 left-1 text-[10px] bg-black/70 text-white">
                          {img.type}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Image className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No images yet</p>
                  <p className="text-muted-foreground text-sm">Generate your first image!</p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Image Preview Dialog */}
      <Dialog open={!!previewImage} onOpenChange={() => setPreviewImage(null)}>
        <DialogContent className="max-w-4xl bg-[#0A0A0B] border-border/50 p-0">
          {previewImage && (
            <>
              <div className="relative">
                <img
                  src={getAssetUrl(previewImage.url || previewImage.image_url)}
                  alt=""
                  className="w-full max-h-[70vh] object-contain"
                />
              </div>
              <div className="p-4 border-t border-border/50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      {previewImage.prompt?.substring(0, 100) || "No prompt"}
                      {previewImage.prompt?.length > 100 ? "..." : ""}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {previewImage.created_at && new Date(previewImage.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownload(previewImage.url || previewImage.image_url)}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </Button>
                    
                    {/* Use in Video Dropdown */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm">
                          <Film className="w-4 h-4 mr-2" />
                          Use in Video
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-[#0A0A0B] border-border/50">
                        <DropdownMenuItem 
                          onClick={() => handleUseInVideo(previewImage, "create")}
                          className="cursor-pointer"
                        >
                          <Sparkles className="w-4 h-4 mr-2 text-[#00F0FF]" />
                          Create Video (AI Generation)
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleUseInVideo(previewImage, "ugc")}
                          className="cursor-pointer"
                        >
                          <Camera className="w-4 h-4 mr-2 text-[#FF0055]" />
                          UGC Ad Studio
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-400 border-red-400/50 hover:bg-red-400/10"
                      onClick={() => {
                        setImageToDelete(previewImage);
                        setPreviewImage(null);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-[#0A0A0B] border-border/50">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Image</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this image? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/20">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Hidden file inputs */}
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept="image/*"
      />
    </motion.div>
  );
}
